import * as React from "react"
import { createPortal } from "react-dom"
import { Copy, Plus, Reply, SmilePlus } from "lucide-react"

import { EmojiPanel, EmojiPicker } from "@/components/chat/emoji-picker"
import { Button } from "@/components/ui/button"
import { QUICK_REACTIONS, rememberEmoji } from "@/lib/emoji"
import { cn } from "@/lib/utils"

/** What a message's actions can do; an absent one is not offered. */
export type MessageActionHandlers = {
  /** Absent for a pending send, which has no id to react to yet. */
  onReact?: (emoji: string) => void
  onReply?: () => void
  /** Absent when there is no text to copy. */
  copyText?: string
  /** Emoji you have already put on the message, shown as pressed. */
  mine: ReadonlySet<string>
}

/**
 * The bar that appears just above a message while the mouse is on it (or
 * focus is inside it): the quick reactions one click away, the full picker,
 * and reply. It floats over the gap above, never over the message being
 * read and never under it, so it costs no space and never shifts the thread.
 *
 * Only for a fine pointer; a touch screen has no hover and gets the
 * long-press menu instead.
 */
export function MessageToolbar({
  onReact,
  onReply,
  mine,
}: MessageActionHandlers) {
  if (!onReact && !onReply) return null

  function react(emoji: string) {
    onReact?.(emoji)
    rememberEmoji(emoji)
  }

  return (
    <div
      role="toolbar"
      aria-label="Действия с сообщением"
      className={cn(
        "absolute bottom-full z-10 hidden translate-y-1.5 items-center gap-0.5 rounded-full border bg-popover p-0.5 shadow-md pointer-fine:flex",
        // Toward the open side: right of someone else's message, left of yours.
        "left-0 group-data-[align=end]/message:right-0 group-data-[align=end]/message:left-auto",
        "pointer-events-none opacity-0 transition-opacity duration-100",
        "group-focus-within/message:pointer-events-auto group-focus-within/message:opacity-100 group-hover/message:pointer-events-auto group-hover/message:opacity-100",
        // Stays while its picker is open, even once the mouse has left.
        "has-data-[state=open]:pointer-events-auto has-data-[state=open]:opacity-100"
      )}
    >
      {onReact && (
        <>
          {QUICK_REACTIONS.map((emoji) => (
            <QuickReaction
              key={emoji}
              emoji={emoji}
              pressed={mine.has(emoji)}
              onClick={() => react(emoji)}
              className="size-7 text-base"
            />
          ))}
          <EmojiPicker
            onPick={onReact}
            closeOnPick
            side="bottom"
            align="center"
            trigger={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Другая реакция"
                className="rounded-full text-muted-foreground"
              >
                <SmilePlus />
              </Button>
            }
          />
        </>
      )}
      {onReact && onReply && <span className="mx-0.5 h-4 w-px bg-border" />}
      {onReply && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Ответить"
          onClick={onReply}
          className="rounded-full text-muted-foreground"
        >
          <Reply />
        </Button>
      )}
    </div>
  )
}

/**
 * The menu a long press (or a right click) opens next to a message: big
 * quick reactions on top, then reply and copy. "+" swaps the actions for
 * the whole catalogue in place, so a rarer emoji is still two taps.
 *
 * Rendered into `document.body`: the transcript's scroller sets
 * `contain: content`, which would otherwise trap and clip a fixed overlay
 * (the lightbox has the same note).
 */
export function MessageMenu({
  anchor,
  onClose,
  onReact,
  onReply,
  copyText,
  mine,
}: MessageActionHandlers & {
  /** The message's element, which the menu sits beside. */
  anchor: HTMLElement
  onClose: () => void
}) {
  const [expanded, setExpanded] = React.useState(false)
  const [position, setPosition] = React.useState<{
    top: number
    left: number
  } | null>(null)
  const cardRef = React.useRef<HTMLDivElement>(null)

  // Above the message when it fits, else below, else pinned to the edge;
  // horizontally lined up with the message's own side, kept on screen.
  React.useLayoutEffect(() => {
    function place() {
      const card = cardRef.current
      if (!card) return
      const margin = 8
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const viewportWidth = window.innerWidth
      const rect = anchor.getBoundingClientRect()
      const { offsetHeight: height, offsetWidth: width } = card
      const mineSide = anchor.closest("[data-align=end]") !== null

      let top = rect.top - height - margin
      if (top < margin) {
        top =
          rect.bottom + margin + height <= viewportHeight - margin
            ? rect.bottom + margin
            : Math.max(margin, viewportHeight - height - margin)
      }

      const preferred = mineSide ? rect.right - width : rect.left
      const left = Math.min(
        Math.max(margin, preferred),
        viewportWidth - width - margin
      )

      setPosition({ top, left })
    }

    place()
    window.addEventListener("resize", place)
    return () => window.removeEventListener("resize", place)
  }, [anchor, expanded])

  React.useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  function react(emoji: string) {
    onReact?.(emoji)
    rememberEmoji(emoji)
    onClose()
  }

  async function copy() {
    onClose()
    try {
      await navigator.clipboard.writeText(copyText ?? "")
    } catch {
      // No clipboard permission (an insecure origin, an old browser).
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      {/* A plain opacity layer, not `bg-black/30`: `color-mix()` falls back
          to solid black on Chrome 109. */}
      <div
        className="absolute inset-0 bg-black opacity-30"
        onClick={onClose}
        onContextMenu={(event) => {
          event.preventDefault()
          onClose()
        }}
      />

      <div
        ref={cardRef}
        role="dialog"
        aria-label="Действия с сообщением"
        className={cn(
          "absolute flex flex-col gap-1 rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-lg",
          expanded
            ? "w-[min(20rem,calc(100vw-1rem))]"
            : "w-max max-w-[calc(100vw-1rem)]",
          // Measured before it is shown, so it never flashes in the wrong place.
          !position && "invisible"
        )}
        style={position ?? undefined}
      >
        {expanded ? (
          <EmojiPanel onPick={react} className="max-h-[min(24rem,70vh)]" />
        ) : (
          <>
            {onReact && (
              <div className="flex items-center gap-0.5">
                {QUICK_REACTIONS.map((emoji) => (
                  <QuickReaction
                    key={emoji}
                    emoji={emoji}
                    pressed={mine.has(emoji)}
                    onClick={() => react(emoji)}
                    className="size-10 text-2xl"
                  />
                ))}
                <button
                  type="button"
                  aria-label="Другая реакция"
                  onClick={() => setExpanded(true)}
                  className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Plus className="size-5" />
                </button>
              </div>
            )}

            {(onReply || copyText) && (
              <div className={cn("flex flex-col", onReact && "border-t pt-1")}>
                {onReply && (
                  <MenuItem
                    icon={<Reply />}
                    label="Ответить"
                    onClick={() => {
                      onClose()
                      onReply()
                    }}
                  />
                )}
                {copyText && (
                  <MenuItem
                    icon={<Copy />}
                    label="Копировать текст"
                    onClick={() => void copy()}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

function QuickReaction({
  emoji,
  pressed,
  onClick,
  className,
}: {
  emoji: string
  pressed: boolean
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      aria-label={pressed ? `Убрать ${emoji}` : `Отреагировать ${emoji}`}
      className={cn(
        "flex items-center justify-center rounded-full leading-none transition-transform outline-none select-none hover:scale-125 focus-visible:ring-2 focus-visible:ring-ring active:scale-95",
        pressed && "bg-accent ring-1 ring-primary",
        className
      )}
    >
      {emoji}
    </button>
  )
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactElement
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors outline-none hover:bg-accent focus-visible:bg-accent [&_svg]:size-4 [&_svg]:text-muted-foreground"
    >
      {icon}
      {label}
    </button>
  )
}
